import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Cloud,
  Filter
} from 'lucide-react';
import { 
  useEnvironmentalRiskFactors, 
  useEnvironmentalRiskFactorMutations,
  useEnvironmentalRiskFactorStatistics 
} from '../../hooks/useEnvironmentalRiskFactors';
import './EnvironmentalRiskFactorManager.css';

const EnvironmentalRiskFactorManager = () => {
  const { t } = useTranslation('patientRisk');
  const [showForm, setShowForm] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  const { factors, loading, refresh } = useEnvironmentalRiskFactors({ 
    activeOnly: !showInactive 
  });
  
  const { statistics, loading: statsLoading } = useEnvironmentalRiskFactorStatistics();
  
  const { 
    createFactor, 
    updateFactor, 
    deleteFactor,
    toggleActive,
    saving 
  } = useEnvironmentalRiskFactorMutations();
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Physical',
    multiplier: 1.3,
    description: '',
    measurementCriteria: '',
    departmentSpecific: [],
    seasonalVariation: false,
    isActive: true
  });
  
  const [errors, setErrors] = useState({});
  
  const handleAddFactor = () => {
    setEditingFactor(null);
    setFormData({
      name: '',
      category: 'Physical',
      multiplier: 1.3,
      description: '',
      measurementCriteria: '',
      departmentSpecific: [],
      seasonalVariation: false,
      isActive: true
    });
    setShowForm(true);
  };
  
  const handleEditFactor = (factor) => {
    setEditingFactor(factor);
    setFormData({
      name: factor.name,
      category: factor.category,
      multiplier: factor.multiplier,
      description: factor.description || '',
      measurementCriteria: factor.measurementCriteria || '',
      departmentSpecific: factor.departmentSpecific || [],
      seasonalVariation: factor.seasonalVariation || false,
      isActive: factor.isActive
    });
    setShowForm(true);
  };
  
  const handleDeleteFactor = async (factorId) => {
    if (!window.confirm('Are you sure you want to delete this environmental risk factor? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteFactor(factorId);
      refresh();
    } catch (error) {
      alert('Failed to delete environmental risk factor. It may be in use.');
    }
  };
  
  const handleToggleActive = async (factorId) => {
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      await toggleActive(factorId, userId);
      refresh();
    } catch (error) {
      alert('Failed to toggle environmental risk factor status.');
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    const multiplier = parseFloat(formData.multiplier);
    if (isNaN(multiplier) || multiplier < 1.0 || multiplier > 2.0) {
      newErrors.multiplier = 'Multiplier must be between 1.0 and 2.0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      const userId = 'current-user'; // TODO: Get from auth context
      
      if (editingFactor) {
        await updateFactor(editingFactor.id, formData, userId);
      } else {
        await createFactor(formData, userId);
      }
      
      setShowForm(false);
      setEditingFactor(null);
      refresh();
    } catch (error) {
      alert(`${t('failedToSaveEnvironmentalFactor')}: ${error.message}`);
    }
  };
  
  const filteredFactors = factors.filter(factor => {
    if (categoryFilter !== 'all' && factor.category !== categoryFilter) {
      return false;
    }
    return true;
  });
  
  const getMultiplierColor = (multiplier) => {
    if (multiplier >= 1.7) return '#f44336';
    if (multiplier >= 1.5) return '#ff9800';
    if (multiplier >= 1.3) return '#ffc107';
    return '#8bc34a';
  };
  
  return (
    <div className="environmental-risk-factor-manager">
      <div className="manager-header">
        <div className="header-title">
          <h1>{t('environmentalRiskFactorLibrary')}</h1>
          <p>{t('manageEnvironmentalRiskFactors')}</p>
        </div>
        <button onClick={handleAddFactor} className="btn btn-primary">
          <Plus size={20} />
          {t('addRiskFactor')}
        </button>
      </div>
      
      {/* Statistics */}
      {!statsLoading && statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{statistics.total}</div>
            <div className="stat-label">{t('totalFactors')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statistics.active}</div>
            <div className="stat-label">{t('active')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statistics.highImpact}</div>
            <div className="stat-label">{t('highImpact15')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statistics.seasonal}</div>
            <div className="stat-label">{t('seasonalFactors')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">×{statistics.avgMultiplier}</div>
            <div className="stat-label">{t('avgMultiplier')}</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="manager-filters">
        <div className="filter-group">
          <Filter size={20} />
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">{t('allCategories')}</option>
            <option value="Physical">{t('categoryPhysical')}</option>
            <option value="Operational">{t('categoryOperational')}</option>
            <option value="Infrastructure">{t('categoryInfrastructure')}</option>
            <option value="Compliance">{t('categoryCompliance')}</option>
          </select>
        </div>
        
        <label className="show-inactive-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {t('showInactiveFactors')}
        </label>
      </div>
      
      {/* Factors List/Table */}
      {loading ? (
        <div className="loading-state">{t('loadingEnvironmentalRiskFactors')}</div>
      ) : filteredFactors.length === 0 ? (
        <div className="empty-state">
          <AlertCircle size={48} color="#ccc" />
          <h3>{t('noEnvironmentalRiskFactorsFound')}</h3>
          <p>{t('addFirstEnvironmentalRiskFactor')}</p>
        </div>
      ) : (
        <div className="factors-table-container">
          <table className="factors-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('category')}</th>
                <th>{t('multiplier')}</th>
                <th>{t('description')}</th>
                <th>{t('measurementCriteria')}</th>
                <th>{t('seasonal')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredFactors.map(factor => (
                <tr 
                  key={factor.id} 
                  className={!factor.isActive ? 'inactive-row' : ''}
                >
                  <td>
                    <div className="factor-name-cell">
                      <strong>{factor.name}</strong>
                      {factor.seasonalVariation && (
                        <Cloud size={14} className="seasonal-icon" title="Seasonal Variation" />
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`category-badge ${factor.category.toLowerCase()}`}>
                      {factor.category}
                    </span>
                  </td>
                  <td>
                    <span 
                      className="multiplier-badge"
                      style={{ backgroundColor: getMultiplierColor(factor.multiplier) }}
                    >
                      ×{factor.multiplier.toFixed(1)}
                    </span>
                  </td>
                  <td className="description-cell">
                    {factor.description || '—'}
                  </td>
                  <td className="measurement-cell">
                    {factor.measurementCriteria || '—'}
                  </td>
                  <td className="text-center">
                    {factor.seasonalVariation ? (
                      <span className="yes-badge">{t('yes')}</span>
                    ) : (
                      <span className="no-badge">{t('no')}</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(factor.id)}
                      className={`btn btn-secondary btn-sm ${factor.isActive ? 'active' : ''}`}
                      title={factor.isActive ? t('deactivate') : t('activate')}
                    >
                      {factor.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      {factor.isActive ? t('active') : t('inactive')}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEditFactor(factor)}
                        className="btn btn-edit btn-icon"
                        title={t('edit')}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteFactor(factor.id)}
                        className="btn btn-delete btn-icon"
                        title={t('delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Form Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-modal">
            <div className="form-header">
              <h2>{editingFactor ? t('editEnvironmentalRiskFactor') : t('addEnvironmentalRiskFactor')}</h2>
              <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                <X size={20} />
              </button>
            </div>
            
            <div className="form-content">
              <div className="form-group">
                <label>{t('name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder={t('placeholderEnvName')}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('category')} *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={errors.category ? 'error' : ''}
                  >
                    <option value="Physical">{t('categoryPhysical')}</option>
                    <option value="Operational">{t('categoryOperational')}</option>
                    <option value="Infrastructure">{t('categoryInfrastructure')}</option>
                    <option value="Compliance">{t('categoryCompliance')}</option>
                  </select>
                  {errors.category && <span className="error-text">{errors.category}</span>}
                </div>
                
                <div className="form-group">
                  <label>{t('multiplierRange12Label')}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1.0"
                    max="2.0"
                    value={formData.multiplier}
                    onChange={(e) => handleInputChange('multiplier', e.target.value)}
                    className={errors.multiplier ? 'error' : ''}
                  />
                  {errors.multiplier && <span className="error-text">{errors.multiplier}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder={t('placeholderEnvDescription')}
                />
              </div>
              
              <div className="form-group">
                <label>{t('measurementCriteria')}</label>
                <textarea
                  value={formData.measurementCriteria}
                  onChange={(e) => handleInputChange('measurementCriteria', e.target.value)}
                  rows={2}
                  placeholder={t('placeholderMeasurementCriteria')}
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.seasonalVariation}
                    onChange={(e) => handleInputChange('seasonalVariation', e.target.checked)}
                  />
                  <span>{t('seasonalVariationLabel')}</span>
                </label>
                <p className="help-text">
                  {t('seasonalVariationHelp')}
                </p>
              </div>
            </div>
            
            <div className="form-footer">
              <button onClick={() => setShowForm(false)} className="btn btn-cancel">
                {t('cancel')}
              </button>
              <button onClick={handleSave} className="btn btn-save" disabled={saving}>
                {saving ? t('savingLabel') : (
                  <>
                    <Save size={16} />
                    {editingFactor ? t('update') : t('createFactor')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentalRiskFactorManager;

